/**
 * Where a panel hung off a control goes, and how tall it may be.
 *
 * A menu tall enough to reach past the bottom of the window cannot be scrolled back into
 * view: the panel it hangs off is sticky, so the page scrolls underneath it and the menu
 * travels with it. Every panel here is therefore measured against the window it opens in
 * rather than given a height and hoped for.
 *
 * Pure arithmetic over rectangles, held apart from the components so the rules can be read
 * and tested without a browser.
 */

/* The window margin, the gap the design leaves under a control, and one row with the
   padding a panel carries around its rows. */
const EDGE = 8;
const GAP = 5;
const ROW = 29;
const PADDING = 10;

export interface View {
  width: number;
  height: number;
}

export interface Drop {
  drop: "below" | "above";
  maxHeight: number;
}

/**
 * Which side of a control a menu opens on, and the height it must live within.
 *
 * Below by default, because that is where the eye goes and where the design draws it. It
 * turns upward only when there is meaningfully more room there, so a menu near the bottom of
 * a short window opens into the space it has rather than off the end of it.
 */
export function dropFrom(
  trigger: { top: number; bottom: number },
  view: View,
  tallest: number,
): Drop {
  const below = view.height - trigger.bottom - GAP - EDGE;
  const above = trigger.top - GAP - EDGE;
  const drop = below >= tallest || below >= above ? "below" : "above";
  return {
    drop,
    maxHeight: Math.min(
      tallest,
      Math.max(drop === "below" ? below : above, ROW),
    ),
  };
}

/** Beside a row, in window coordinates. */
export interface Beside {
  left: number;
  top: number;
  maxHeight: number;
}

/**
 * Where a row's own list sits: beside it, inside the window, level with it where it can be.
 *
 * The side flips when the room to the right runs out, and the top rides up when the list
 * would otherwise hang past the bottom. Height is measured from the rows themselves, so a
 * short list is not pushed further up than its own length requires.
 */
export function besideRow(
  row: { left: number; right: number; top: number },
  view: View,
  rows: number,
  width: number,
  tallest: number,
): Beside {
  const wanted = Math.min(tallest, rows * ROW + PADDING);
  const room = view.width - row.right - EDGE;
  const left = room >= width ? row.right : Math.max(EDGE, row.left - width);
  const lowest = view.height - EDGE - wanted;
  return {
    left,
    top: Math.max(EDGE, Math.min(row.top, lowest)),
    maxHeight: Math.min(tallest, view.height - 2 * EDGE),
  };
}
